// Typed API surface for the Production + Pack-conversion screens (plan.md G21).
// Every hook is built from the two shared primitives (src/hooks/useApi.ts) over the
// typed fetch wrapper (src/api/client.ts) — no direct `fetch`/`Response` handling
// here, matching the pattern established by every other web module goal. Talks to:
//   - services/api/src/modules/production/{stages,recipes,runs}.ts (this goal's
//     production domain — process stages, stage recipes, production runs + lines).
//   - services/api/src/modules/packconv/conversions.ts (pack conversions, §8.5).
//   - services/api/src/modules/config/{sites,locations,products}.ts — read-only
//     lookups + the pack-spec nested resource (own copies per S0 — see types.ts's
//     header for why these aren't imported from a sibling feature).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CloseProductionRunInput,
  CreateProcessStageInput,
  CreateStageRecipeInput,
  MovementSource,
  OpenProductionRunInput,
  PackConversionPostResponse,
  PackConversionResponse,
  PackConversionReverseResponse,
  PackConversionUpdate,
  PackConversionWrite,
  ProductionRunStatus,
  ReverseProductionRunResponse,
  RunConsumableLineInput,
  RunInputLineInput,
  RunOutputLineInput,
  UpdateProcessStageInput,
  UpsertPackSpecInput,
} from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../api/client';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type {
  ListResponse,
  LocationDto,
  PackSpecDto,
  PaginatedResponse,
  ProductDto,
  RecipeDto,
  RunConsumableLineDto,
  RunDetailDto,
  RunDto,
  RunInputLineDto,
  RunOutputLineDto,
  SiteDto,
  StageDto,
  PostRunResponse,
} from './types';

const LOOKUP_LIMIT = 200;

// ---------------------------------------------------------------------------
// Config lookups (config module, G13) — read-only.
// ---------------------------------------------------------------------------

export function useSites() {
  return useApiQuery<PaginatedResponse<SiteDto>>(['config', 'sites'], '/config/sites', {
    query: { limit: LOOKUP_LIMIT },
  });
}

export function useLocations(siteId: string | undefined) {
  return useApiQuery<PaginatedResponse<LocationDto>>(['config', 'locations', siteId ?? 'all'], '/config/locations', {
    query: { siteId, isActive: true, limit: LOOKUP_LIMIT },
    enabled: Boolean(siteId),
  });
}

/** Every active location tenant-wide (unfiltered by site) — used by the pack
 * conversions register to resolve a location name without knowing its site up
 * front. */
export function useAllLocations() {
  return useApiQuery<PaginatedResponse<LocationDto>>(['config', 'locations', 'all-sites'], '/config/locations', {
    query: { isActive: true, limit: LOOKUP_LIMIT },
  });
}

export function useActiveProducts() {
  return useApiQuery<PaginatedResponse<ProductDto>>(['config', 'products', 'active'], '/config/products', {
    query: { isActive: true, limit: LOOKUP_LIMIT },
  });
}

/** Every product tenant-wide, active AND inactive (`isActive` omitted — the
 * endpoint filters only when the param is present; services/api/src/modules/
 * config/products.ts) — for LOOKUP MAPS only: resolving the name/baseUom of a
 * productId already recorded on a run/spec line. The server's close/post math
 * joins the FULL products table with no isActive filter (services/api/src/
 * modules/production/runs.ts's base_uom joins), so a lookup scoped to active
 * products would silently drop a line whose product was deactivated after the
 * run opened — diverging the client preview from what the server actually
 * posts. Product PICKERS must keep using `useActiveProducts`: deactivated
 * products must not become selectable. */
export function useAllProducts() {
  return useApiQuery<PaginatedResponse<ProductDto>>(['config', 'products', 'all'], '/config/products', {
    query: { limit: LOOKUP_LIMIT },
  });
}

// ---------------------------------------------------------------------------
// Pack specs — nested 1:1 resource on a pack (SKU) product (config module, G13).
// This feature only consumes + writes the existing endpoint (same precedent as
// features/silo-counts/api.ts's silo-calibration nested resource); it does not
// build a general products/pack-specs settings screen.
// ---------------------------------------------------------------------------

export function usePackSpec(packProductId: string | null) {
  return useApiQuery<PackSpecDto>(
    ['config', 'products', packProductId, 'pack-spec'],
    `/config/products/${packProductId}/pack-spec`,
    { enabled: Boolean(packProductId), retry: false },
  );
}

export function isPackSpecMissing(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'NOT_FOUND';
}

export function useUpsertPackSpec(packProductId: string) {
  const qc = useQueryClient();
  return useApiMutation<PackSpecDto, UpsertPackSpecInput>(`/config/products/${packProductId}/pack-spec`, 'PUT', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config', 'products', packProductId, 'pack-spec'] }),
  });
}

// ---------------------------------------------------------------------------
// Process stages (production module, G7) — this goal's sole web ownership.
// ---------------------------------------------------------------------------

export function useStages() {
  return useApiQuery<ListResponse<StageDto>>(['production', 'stages'], '/production/stages');
}

export function useStage(id: string | undefined) {
  return useApiQuery<StageDto>(['production', 'stages', 'byId', id ?? null], `/production/stages/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateStage() {
  const qc = useQueryClient();
  return useApiMutation<StageDto, CreateProcessStageInput>('/production/stages', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'stages'] }),
  });
}

export function useUpdateStage(id: string) {
  const qc = useQueryClient();
  return useApiMutation<StageDto, UpdateProcessStageInput>(`/production/stages/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'stages'] }),
  });
}

// ---------------------------------------------------------------------------
// Stage recipes (production module, G7) — nested under a stage.
// ---------------------------------------------------------------------------

export function useRecipesForStage(stageId: string | undefined) {
  return useApiQuery<ListResponse<RecipeDto>>(
    ['production', 'stages', stageId ?? 'none', 'recipes'],
    `/production/stages/${stageId}/recipes`,
    { enabled: Boolean(stageId) },
  );
}

export function useRecipe(id: string | undefined) {
  return useApiQuery<RecipeDto>(['production', 'recipes', 'byId', id ?? null], `/production/recipes/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateRecipe(stageId: string) {
  const qc = useQueryClient();
  return useApiMutation<RecipeDto, CreateStageRecipeInput>(`/production/stages/${stageId}/recipes`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'stages', stageId, 'recipes'] }),
  });
}

export function useActivateRecipe(recipeId: string, stageId: string) {
  const qc = useQueryClient();
  return useApiMutation<RecipeDto, void>(`/production/recipes/${recipeId}/activate`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'stages', stageId, 'recipes'] }),
  });
}

// ---------------------------------------------------------------------------
// Production runs (+ lines) — services/api/src/modules/production/runs.ts
// ---------------------------------------------------------------------------

export interface RunsListParams {
  siteId?: string;
  stageId?: string;
  status?: ProductionRunStatus;
}

export function useRuns(params: RunsListParams = {}) {
  return useApiQuery<ListResponse<RunDto>>(
    ['production', 'runs', params.siteId ?? 'all', params.stageId ?? 'all', params.status ?? 'all'],
    '/production/runs',
    { query: { siteId: params.siteId, stageId: params.stageId, status: params.status } },
  );
}

export function useRun(id: string | undefined) {
  return useApiQuery<RunDetailDto>(['production', 'runs', 'byId', id ?? null], `/production/runs/${id}`, {
    enabled: Boolean(id),
  });
}

export function useOpenRun() {
  const qc = useQueryClient();
  return useApiMutation<RunDto, OpenProductionRunInput>('/production/runs', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'runs'] }),
  });
}

function invalidateRun(qc: ReturnType<typeof useQueryClient>, runId: string) {
  qc.invalidateQueries({ queryKey: ['production', 'runs', 'byId', runId] });
  qc.invalidateQueries({ queryKey: ['production', 'runs'] });
}

export function useAddRunInput(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<RunInputLineDto, RunInputLineInput>(`/production/runs/${runId}/inputs`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useDeleteRunInput(runId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (lineId) => apiFetch<void>(`/production/runs/${runId}/inputs/${lineId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useAddRunOutput(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<RunOutputLineDto, RunOutputLineInput>(`/production/runs/${runId}/outputs`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useDeleteRunOutput(runId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (lineId) => apiFetch<void>(`/production/runs/${runId}/outputs/${lineId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useAddRunConsumable(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<RunConsumableLineDto, RunConsumableLineInput>(`/production/runs/${runId}/consumables`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useDeleteRunConsumable(runId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (lineId) => apiFetch<void>(`/production/runs/${runId}/consumables/${lineId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useCloseRun(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<RunDto, CloseProductionRunInput>(`/production/runs/${runId}/close`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function usePostRun(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<PostRunResponse, void>(`/production/runs/${runId}/post`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export function useReverseRun(runId: string) {
  const qc = useQueryClient();
  return useApiMutation<ReverseProductionRunResponse, void>(`/production/runs/${runId}/reverse`, 'POST', {
    onSuccess: () => invalidateRun(qc, runId),
  });
}

export interface ReverseAndCloneRunResult {
  newRunId: string;
  newRunNumber: string;
}

/** "Reverse & clone" for a POSTED production run (BUILD_SPEC.md §4 core principle
 * #1; plan.md QC fix #10) — reverses the run's ledger movements, then opens a FRESH
 * run under the same stage and copies every input/output/consumable line across as
 * a starting point, so the operator lands on the new run's detail page (still OPEN)
 * to review/correct before closing+posting again. Composed client-side from several
 * dedicated endpoints (fetch detail, reverse, open, per-line add) — mirrors
 * purchasing-intake's `useReceiveLoad` composite-mutation precedent for the
 * identical reason (one isPending/error/data lifecycle for a multi-call user
 * action). Fetches the run's lines itself (rather than taking a caller-supplied
 * `RunDetailDto`) so a register row never needs its own `useRun` subscription just
 * to make this button available. */
export function useReverseAndCloneRun(runId: string) {
  const qc = useQueryClient();
  return useMutation<ReverseAndCloneRunResult, Error, void>({
    mutationFn: async () => {
      const run = await apiFetch<RunDetailDto>(`/production/runs/${runId}`);
      await apiFetch(`/production/runs/${runId}/reverse`, { method: 'POST' });

      const openBody: OpenProductionRunInput = {
        siteId: run.siteId,
        stageId: run.stageId,
        shiftLabel: run.shiftLabel,
        notes: `Reversed & cloned from ${run.number}`,
      };
      const newRun = await apiFetch<RunDto>('/production/runs', { method: 'POST', body: openBody });

      for (const line of run.inputs) {
        const body: RunInputLineInput = {
          productId: line.productId,
          fromLocationId: line.fromLocationId,
          qtyKg: line.qtyKg,
          mcPct: line.mcPct,
          source: line.source,
          handlingUnits: line.handlingUnits,
        };
        await apiFetch(`/production/runs/${newRun.id}/inputs`, { method: 'POST', body });
      }
      for (const line of run.outputs) {
        const body: RunOutputLineInput = {
          productId: line.productId,
          toLocationId: line.toLocationId,
          qtyKg: line.qtyKg,
          mcPct: line.mcPct,
          handlingUnits: line.handlingUnits,
          isPrimary: line.isPrimary,
        };
        await apiFetch(`/production/runs/${newRun.id}/outputs`, { method: 'POST', body });
      }
      for (const line of run.consumables) {
        const body: RunConsumableLineInput = {
          productId: line.productId,
          fromLocationId: line.fromLocationId,
          qty: line.qty,
          source: 'ACTUAL',
        };
        await apiFetch(`/production/runs/${newRun.id}/consumables`, { method: 'POST', body });
      }

      return { newRunId: newRun.id, newRunNumber: newRun.number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'runs'] }),
  });
}

// ---------------------------------------------------------------------------
// "Open kiln batch" (§8.3 BATCH mode) — a composite action spanning two dedicated
// endpoints (open the run, then record the cages-in input line), tied together as
// one user action the same way purchasing-intake's `useReceiveLoad` composes
// ticket+GR+post. Not a single server route — production_run_inputs requires a
// runId, so the run must exist first.
// ---------------------------------------------------------------------------

export interface OpenBatchInput {
  siteId: string;
  stageId: string;
  productId: string;
  fromLocationId: string;
  qtyKg: string;
  mcPct: string | null;
  handlingUnits: number | null;
  source: MovementSource;
}

export function useOpenBatch() {
  const qc = useQueryClient();
  return useMutation<RunDto, Error, OpenBatchInput>({
    mutationFn: async (input) => {
      const openBody: OpenProductionRunInput = { siteId: input.siteId, stageId: input.stageId };
      const run = await apiFetch<RunDto>('/production/runs', { method: 'POST', body: openBody });

      const inputBody: RunInputLineInput = {
        productId: input.productId,
        fromLocationId: input.fromLocationId,
        qtyKg: input.qtyKg,
        mcPct: input.mcPct,
        source: input.source,
        handlingUnits: input.handlingUnits,
      };
      await apiFetch(`/production/runs/${run.id}/inputs`, { method: 'POST', body: inputBody });

      return run;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'runs'] }),
  });
}

// ---------------------------------------------------------------------------
// "Quick conversion" — Stuart ruling 2026-08-26: Howick runs ~8 conversions/day
// (e.g. kiln drying) but does NOT weigh loads in/out; operators estimate via the
// stage's active recipe's historic ratio. One screen, ONE typed number (input
// kg), and this composes the SAME five endpoints RecordShiftWizard drives one
// step at a time (open -> input -> output -> close -> post) into a single
// action — no new API surface, no new posting path. Same composite-mutation
// shape as `useOpenBatch` above, but must also survive a failure PARTWAY
// through: once step 1 succeeds the run is OPEN server-side, and per spec
// that's an acceptable outcome (the operator finishes it in the full run
// editor) rather than something to roll back — so a later step's failure is
// re-thrown as `QuickConversionError`, carrying that run id, instead of a bare
// `Error` that would strand the operator with no way back to the half-built
// run.
// ---------------------------------------------------------------------------

export interface QuickConversionInput {
  siteId: string;
  stageId: string;
  shiftLabel: string | null;
  notes: string | null;
  /** Maps to BOTH `startedAt` (open) and `endedAt` (close) — a quick conversion
   * is an estimated ratio-based entry, not a timed shift, so there is only one
   * meaningful timestamp. `null` lets the server default each independently to
   * "now" (a few ms apart across the two calls, immaterial here). */
  occurredAt: Date | null;
  inputProductId: string;
  fromLocationId: string;
  inputQtyKg: string;
  inputMcPct: string | null;
  outputProductId: string;
  toLocationId: string;
  outputQtyKg: string;
  outputMcPct: string | null;
  /** Required when the pinned recipe has consumable lines (mirrors
   * ClosePostPanel's identical close-time requirement) — this composite never
   * records consumables as their own step, so every recipe consumable is
   * "missing" and gets backflushed at close. */
  consumablesFromLocationId: string | null;
}

export interface QuickConversionResult {
  runId: string;
  runNumber: string;
  post: PostRunResponse;
}

/** Thrown when a step AFTER the run was opened fails — `runId` lets the caller
 * link straight to the now-OPEN run in the full editor instead of just showing
 * an error with no way back to it. */
export class QuickConversionError extends Error {
  readonly runId: string;

  constructor(message: string, runId: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'QuickConversionError';
    this.runId = runId;
  }
}

export function useQuickConversion() {
  const qc = useQueryClient();
  return useMutation<QuickConversionResult, Error, QuickConversionInput>({
    mutationFn: async (input) => {
      const openBody: OpenProductionRunInput = {
        siteId: input.siteId,
        stageId: input.stageId,
        shiftLabel: input.shiftLabel,
        startedAt: input.occurredAt ?? undefined,
        notes: input.notes,
      };
      const run = await apiFetch<RunDto>('/production/runs', { method: 'POST', body: openBody });

      try {
        const inputBody: RunInputLineInput = {
          productId: input.inputProductId,
          fromLocationId: input.fromLocationId,
          qtyKg: input.inputQtyKg,
          mcPct: input.inputMcPct,
          source: 'MEASURED',
          handlingUnits: null,
        };
        await apiFetch(`/production/runs/${run.id}/inputs`, { method: 'POST', body: inputBody });

        const outputBody: RunOutputLineInput = {
          productId: input.outputProductId,
          toLocationId: input.toLocationId,
          qtyKg: input.outputQtyKg,
          mcPct: input.outputMcPct,
          handlingUnits: null,
          isPrimary: true,
        };
        await apiFetch(`/production/runs/${run.id}/outputs`, { method: 'POST', body: outputBody });

        const closeBody: CloseProductionRunInput = {
          endedAt: input.occurredAt ?? undefined,
          downtimeMinutes: null,
          consumablesFromLocationId: input.consumablesFromLocationId,
        };
        await apiFetch(`/production/runs/${run.id}/close`, { method: 'POST', body: closeBody });

        const post = await apiFetch<PostRunResponse>(`/production/runs/${run.id}/post`, { method: 'POST' });
        return { runId: run.id, runNumber: run.number, post };
      } catch (cause) {
        const message =
          cause instanceof ApiRequestError ? cause.message : 'Quick conversion failed after the run was opened';
        throw new QuickConversionError(message, run.id, { cause });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production', 'runs'] }),
    // A partial failure still leaves a real OPEN run behind (by design) — the
    // Runs register should reflect it immediately, same as a fully-successful
    // conversion, in case the operator navigates there before following the
    // error alert's link.
    onError: () => qc.invalidateQueries({ queryKey: ['production', 'runs'] }),
  });
}

// ---------------------------------------------------------------------------
// Pack conversions (packconv module, G12) — services/api/src/modules/packconv/
// conversions.ts, mounted at /api/v1/packconv.
// ---------------------------------------------------------------------------

const packConversionsListKey = ['packconv', 'conversions', 'list'] as const;

export function usePackConversions(cursor: string | undefined) {
  return useApiQuery<PaginatedResponse<PackConversionResponse>>(
    [...packConversionsListKey, cursor ?? null],
    '/packconv',
    { query: { cursor, limit: 20 } },
  );
}

export function usePackConversion(id: string | undefined) {
  return useApiQuery<PackConversionResponse>(['packconv', 'conversions', 'byId', id ?? null], `/packconv/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreatePackConversion() {
  const qc = useQueryClient();
  return useApiMutation<PackConversionResponse, PackConversionWrite>('/packconv', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}

export function useUpdatePackConversion(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PackConversionResponse, PackConversionUpdate>(`/packconv/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}

export function usePostPackConversion(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PackConversionPostResponse, void>(`/packconv/${id}/post`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}

export function useReversePackConversion(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PackConversionReverseResponse, void>(`/packconv/${id}/reverse`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}

/** Composite create+post used by the guided "Pack conversion" flow — a single
 * mutation (rather than two separately-instantiated hooks) because the post step
 * needs the id the create step JUST returned; two hooks bound to different
 * `useApiMutation` path instances would close over a stale (pre-create) id. */
export interface CreateAndPostPackConversionResult {
  conversion: PackConversionResponse;
  post: PackConversionPostResponse;
}

export function useCreateAndPostPackConversion() {
  const qc = useQueryClient();
  return useMutation<CreateAndPostPackConversionResult, Error, PackConversionWrite>({
    mutationFn: async (body) => {
      const conversion = await apiFetch<PackConversionResponse>('/packconv', { method: 'POST', body });
      const post = await apiFetch<PackConversionPostResponse>(`/packconv/${conversion.id}/post`, { method: 'POST' });
      return { conversion, post };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}

/** "Reverse & clone" for a POSTED pack conversion (plan.md QC fix #10) — mirrors
 * ReceiptsRegister's identical goods-receipt clone pattern exactly: reverse, then
 * create a fresh DRAFT with the same header fields, left un-posted for review. */
export function useReverseAndClonePackConversion(conversion: PackConversionResponse) {
  const qc = useQueryClient();
  return useMutation<PackConversionResponse, Error, void>({
    mutationFn: async () => {
      await apiFetch(`/packconv/${conversion.id}/reverse`, { method: 'POST' });
      const body: PackConversionWrite = {
        packSpecId: conversion.packSpecId,
        numPacks: conversion.numPacks,
        locationId: conversion.locationId,
        occurredAt: new Date(),
        notes: `Reversed & cloned from ${conversion.number}`,
      };
      return apiFetch<PackConversionResponse>('/packconv', { method: 'POST', body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: packConversionsListKey }),
  });
}
